// @ts-nocheck - Temporarily disable type checking for this file due to Capacitor type definition issues
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { db } from '../firebase/config';
import { getTokens } from '../utils/tokenStorage';
import { firestoreRestApi } from '../utils/firestoreRestApi';

// Suppress in-app troubleshooting popups during physical device testing/deployment.
const alert = (_message?: unknown): void => {};

// Helper function to safely remove listeners (workaround for TypeScript type issues)
// This function handles the type mismatch between Capacitor's type definitions and runtime behavior
const removeListener = async (listener: any): Promise<void> => {
  if (!listener) return;
  
  try {
    // Handle both PluginListenerHandle and Promise<PluginListenerHandle> cases
    const handle = listener instanceof Promise ? await listener : listener;
    
    // Use bracket notation to avoid TypeScript error
    const removeMethod = (handle as any)?.remove;
    if (removeMethod && typeof removeMethod === 'function') {
      await removeMethod.call(handle);
    }
  } catch (error) {
    console.warn('[NotificationService] Error removing listener:', error);
  }
};

class NotificationService {
  private messaging: Messaging | null = null;
  private pushNotificationListeners: any[] = [];
  private nativeTokenReceived: string | null = null;
  private nativeTokenPromise: Promise<string> | null = null;
  private pendingTokenToSave: string | null = null; // Token received before user logged in
  private _debugCallback: ((step: string) => void) | null = null;

  /** Register a callback that receives each iOS debug step directly (no window events). */
  setDebugCallback(cb: ((step: string) => void) | null) {
    this._debugCallback = cb;
  }

  constructor() {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') {
      // Web: Use Firebase Messaging
      try {
        this.messaging = getMessaging();
        this.setupMessageListener();
        this.registerServiceWorker();
      } catch (error) {
        console.log('Firebase Messaging not available:', error);
      }
    } else {
      // iOS/Android: Set up listeners for push notifications
      this.setupNativePushListeners();
    }
  }

  /**
   * Set up listeners for native push notifications (iOS/Android)
   * These listeners handle incoming notifications and token registration
   */
  setupNativePushListeners = (): void => {
    const platform = Capacitor.getPlatform();
    
    // Listen for push notifications when app is in foreground
    const pushReceivedListener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received: ', notification);
      // Notification is automatically shown by the OS when app is in background
      // When in foreground, we can show a custom notification if needed
    });

    // Listen for push notification actions (when user taps notification)
    const pushActionPerformedListener = PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Push notification action performed', notification.actionId, notification.inputValue);
    });

    // On iOS: Listen for FCM token received from AppDelegate and save it automatically
    if (platform === 'ios') {
      const fcmTokenListener = (event: any) => {
        const token = event.detail?.fcmToken || event.detail?.token || (event as any).fcmToken;
        if (token) {
          console.log('[NotificationService] 🔥 FCM Token received during app launch:', token);
          // Store token in ALL locations to ensure it's found later
          this.nativeTokenReceived = token;
          this.pendingTokenToSave = token;
          (window as any).__FCM_TOKEN__ = token;
          console.log('[NotificationService] Token stored in all locations: nativeTokenReceived, pendingTokenToSave, window.__FCM_TOKEN__');

          // Try to save token if user is logged in
          this.saveTokenIfUserLoggedIn(token).catch((error) => {
            console.warn('[NotificationService] Could not save token automatically (user may not be logged in yet):', error);
            // Don't show error alert here - token is stored and will be saved later
            console.log('[NotificationService] Token stored for later save when user logs in or enables notifications');
          });
        }
      };
      
      window.addEventListener('FirebaseMessagingTokenReceived', fcmTokenListener);
      this.pushNotificationListeners.push({
        remove: () => window.removeEventListener('FirebaseMessagingTokenReceived', fcmTokenListener)
      });
      
      // Also check if token is already available
      const existingToken = (window as any).__FCM_TOKEN__;
      if (existingToken) {
        console.log('[NotificationService] 🔥 FCM Token found in global variable during setup:', existingToken);
        // Store in ALL locations to ensure it's found later
        this.nativeTokenReceived = existingToken;
        this.pendingTokenToSave = existingToken;
        console.log('[NotificationService] Token stored in all locations: nativeTokenReceived, pendingTokenToSave');
        this.saveTokenIfUserLoggedIn(existingToken).catch((error) => {
          console.warn('[NotificationService] Could not save existing token (user may not be logged in yet):', error);
          // Don't show error alert - token is stored and will be saved later
          console.log('[NotificationService] Token stored for later save when user logs in or enables notifications');
        });
      }
    }

    // Store listeners for cleanup
    this.pushNotificationListeners.push(
      pushReceivedListener,
      pushActionPerformedListener
    );
  };

  /**
   * Save FCM token if user is logged in
   * This is called automatically when token is received during app launch
   * If user is not logged in, stores token for later saving
   */
  saveTokenIfUserLoggedIn = async (token: string): Promise<void> => {
    try {
      console.log('[NotificationService] Attempting to save token automatically...');
      // Try to get user ID from auth
      const { auth } = await import('../firebase/config');
      let userId = auth.currentUser?.uid;
      
      if (!userId) {
        // Try to get user ID from stored tokens
        const tokens = await getTokens();
        if (tokens && tokens.userId) {
          userId = tokens.userId;
          console.log('[NotificationService] Found userId in stored tokens:', userId);
        }
      }
      
      if (userId) {
        console.log('[NotificationService] User is logged in, saving token automatically...');
        try {
          await this.saveTokenToDatabase(userId, token);
          console.log('[NotificationService] ✅ Token saved automatically during app launch');
          this.pendingTokenToSave = null;
        } catch (saveError) {
          console.error('[NotificationService] ❌ Error saving token to database:', saveError);
          this.pendingTokenToSave = token;
          throw saveError;
        }
      } else {
        console.log('[NotificationService] ⚠️ No user logged in yet, storing token for later save');
        this.pendingTokenToSave = token;
        // Don't throw error - this is expected if user hasn't logged in yet
      }
    } catch (error) {
      console.error('[NotificationService] Error in saveTokenIfUserLoggedIn:', error);
      // Store token for retry even if there was an error
      if (!this.pendingTokenToSave) {
        this.pendingTokenToSave = token;
      }
      throw error;
    }
  };

  /**
   * Try to save pending token if user is now logged in
   * Call this after user logs in
   */
  trySavePendingToken = async (userId: string): Promise<void> => {
    const platform = Capacitor.getPlatform();
    console.log('[NotificationService] trySavePendingToken called for userId:', userId);
    console.log('[NotificationService] pendingTokenToSave:', this.pendingTokenToSave ? `${this.pendingTokenToSave.substring(0, 20)}...` : 'null');
    console.log('[NotificationService] nativeTokenReceived:', this.nativeTokenReceived ? `${this.nativeTokenReceived.substring(0, 20)}...` : 'null');
    console.log('[NotificationService] window.__FCM_TOKEN__:', (window as any).__FCM_TOKEN__ ? `${(window as any).__FCM_TOKEN__.substring(0, 20)}...` : 'null');
    
    // Get the most recent token from any source - check all sources explicitly
    let tokenToSave: string | null = null;
    
    if (this.pendingTokenToSave) {
      tokenToSave = this.pendingTokenToSave;
      console.log('[NotificationService] Using token from pendingTokenToSave');
    } else if (this.nativeTokenReceived) {
      tokenToSave = this.nativeTokenReceived;
      console.log('[NotificationService] Using token from nativeTokenReceived');
    } else if ((window as any).__FCM_TOKEN__) {
      tokenToSave = (window as any).__FCM_TOKEN__;
      console.log('[NotificationService] Using token from window.__FCM_TOKEN__');
    }
    
    if (tokenToSave) {
      console.log('[NotificationService] Found token to save, attempting to save...');
      try {
        await this.saveTokenToDatabase(userId, tokenToSave);
        console.log('[NotificationService] ✅ Token saved successfully after login');
        this.pendingTokenToSave = null;
        this.nativeTokenReceived = null;
      } catch (error) {
        console.error('[NotificationService] ❌ Error saving token after login:', error);
        if (!this.pendingTokenToSave) {
          this.pendingTokenToSave = tokenToSave;
        }
        throw error;
      }
    } else {
      console.log('[NotificationService] No token available to save');
      console.log('[NotificationService] All token sources checked:');
      console.log('[NotificationService]   - pendingTokenToSave:', !!this.pendingTokenToSave);
      console.log('[NotificationService]   - nativeTokenReceived:', !!this.nativeTokenReceived);
      console.log('[NotificationService]   - window.__FCM_TOKEN__:', !!(window as any).__FCM_TOKEN__);
      // Silent no-token case on iOS; this is expected when notifications are not enabled yet.
    }
  };
  
  /**
   * Force save current token to Firestore
   * Useful for debugging or manual save
   */
  forceSaveToken = async (userId: string): Promise<{ success: boolean; error?: string }> => {
    console.log('[NotificationService] forceSaveToken called for userId:', userId);
    
    const tokenToSave = this.nativeTokenReceived || this.pendingTokenToSave || (window as any).__FCM_TOKEN__;
    
    if (!tokenToSave) {
      return {
        success: false,
        error: 'No FCM token available to save'
      };
    }
    
    try {
      await this.saveTokenToDatabase(userId, tokenToSave);
      this.pendingTokenToSave = null;
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  /**
   * Register the Firebase messaging service worker
   */
  registerServiceWorker = async (): Promise<void> => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service worker registered successfully:', registration);
      } catch (error) {
        console.error('Service worker registration failed:', error);
      }
    }
  };

  /**
   * Request notification permission and get FCM/push token
   * First checks if we already have a token saved in Firestore
   */
  requestPermission = async (userId: string): Promise<{ success: boolean; token?: string; error?: string }> => {
    const platform = Capacitor.getPlatform();
    const isIOS = platform === 'ios';

    if (isIOS) this.iosDebugStep('A: requestPermission started');

    // Check in-memory token first (no network needed — instant)
    const memToken = this.pendingTokenToSave || this.nativeTokenReceived || (window as any).__FCM_TOKEN__;
    if (memToken) {
      if (isIOS) this.iosDebugStep(`A: ✅ Token already in memory (${memToken.substring(0, 12)}...) — saving`);
      try {
        await this.saveTokenToDatabase(userId, memToken);
        this.pendingTokenToSave = null;
        this.nativeTokenReceived = null;
        if (isIOS) this.iosDebugStep('A: ✅ Memory token saved — done!');
        return { success: true, token: memToken };
      } catch (err) {
        if (isIOS) this.iosDebugStep(`A: ❌ Memory token save failed: ${err instanceof Error ? err.message : String(err)}`);
        return { success: false, error: err instanceof Error ? err.message : 'Save failed' };
      }
    }

    // Check Firestore for an existing platform token.
    // On native, use REST API (SDK silently hangs). Wrap in our withTimeout so
    // even getIdToken() / Capacitor Preferences hangs are caught.
    if (isIOS) this.iosDebugStep('B: No memory token — fetching Firestore doc via REST API...');
    let userData: any = null;
    try {
      const userRef = doc(db, 'users', userId);
      if (platform !== 'web') {
        userData = await this.withTimeout(
          firestoreRestApi.getDocument('users', userId),
          10000,
          'REST getDocument (requestPermission)'
        );
        if (isIOS) this.iosDebugStep(userData ? 'B: ✅ Firestore doc retrieved' : 'B: ⚠️ Timed out or doc not found — skipping existing-token check');
      } else {
        const userDoc = await Promise.race([
          getDoc(userRef),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('getDoc timeout')), 5000)),
        ]);
        userData = userDoc.data();
      }
    } catch (error) {
      if (isIOS) this.iosDebugStep(`B: ⚠️ Error fetching doc: ${error instanceof Error ? error.message : String(error)} — continuing`);
      console.warn('[NotificationService] Could not check for existing token:', error);
    }

    const existingToken = userData?.fcmTokens?.[platform];
    if (existingToken) {
      if (isIOS) this.iosDebugStep(`B: ✅ Existing token found (${existingToken.substring(0, 12)}...) — marking enabled`);
      try {
        if (platform !== 'web') {
          await this.withTimeout(
            firestoreRestApi.setDocument('users', userId, { notificationsEnabled: true, updatedAt: new Date().toISOString() }, true),
            10000,
            'REST setDocument notificationsEnabled'
          );
        } else {
          await updateDoc(doc(db, 'users', userId), { notificationsEnabled: true, updatedAt: new Date() });
        }
        if (isIOS) this.iosDebugStep('B: ✅ notificationsEnabled written — done!');
      } catch (e: any) {
        if (isIOS) this.iosDebugStep(`B: ⚠️ Could not write notificationsEnabled: ${e?.message || String(e)}`);
      }
      return { success: true, token: existingToken };
    }

    if (isIOS) this.iosDebugStep('C: No existing token — calling requestNativePermission...');
    console.log('[NotificationService] No existing token found, requesting new token...');

    if (platform === 'web') {
      return this.requestWebPermission(userId);
    } else {
      return this.requestNativePermission(userId);
    }
  };

  /**
   * Request permission for web (Firebase Messaging)
   */
  requestWebPermission = async (userId: string): Promise<{ success: boolean; token?: string; error?: string }> => {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        return {
          success: false,
          error: 'This browser does not support notifications'
        };
      }

      // Check if service worker is supported
      if (!('serviceWorker' in navigator)) {
        return {
          success: false,
          error: 'Service workers are not supported in this browser'
        };
      }

      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        return {
          success: false,
          error: 'Notification permission denied'
        };
      }

      // Get FCM token
      if (!this.messaging) {
        return {
          success: false,
          error: 'Messaging not initialized'
        };
      }

      // Get the registration token
      const token = await getToken(this.messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
      });

      if (!token) {
        return {
          success: false,
          error: 'Failed to get FCM token'
        };
      }

      // Save token to user document
      await this.saveTokenToDatabase(userId, token);

      console.log('FCM token obtained and saved:', token);
      
      return {
        success: true,
        token
      };
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  /**
   * Request permission for native platforms (iOS/Android)
   * For iOS: Uses Firebase Cloud Messaging SDK directly (bypasses Capacitor plugin)
   * For Android: Uses Capacitor Push Notifications plugin
   */
  requestNativePermission = async (userId: string): Promise<{ success: boolean; token?: string; error?: string }> => {
    const startTime = Date.now();
    try {
      const platform = Capacitor.getPlatform();
      console.log(`[NotificationService] Starting native permission request for platform: ${platform}`);
      console.log(`[NotificationService] Timestamp: ${new Date().toISOString()}`);
      
      // For iOS: Use Firebase Cloud Messaging SDK directly
      if (platform === 'ios') {
        return await this.requestIOSPermissionWithFirebase(userId, startTime);
      }
      
      // For Android: Use Capacitor Push Notifications plugin (existing implementation)
      return await this.requestAndroidPermissionWithCapacitor(userId, startTime);
    } catch (error) {
      console.error('[NotificationService] Error requesting native notification permission:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  /** Emit a debug step — calls the registered callback directly (reliable) and also dispatches a window event. */
  private iosDebugStep(step: string) {
    console.log('[iOS Debug]', step);
    // Direct callback is primary — no event bus races
    if (this._debugCallback) {
      try { this._debugCallback(step); } catch (_) {}
    }
    // Keep window event as secondary
    try { window.dispatchEvent(new CustomEvent('ios-notif-debug', { detail: { step } })); } catch (_) {}
  }

  /** Wrap a promise with a timeout; resolves with null on timeout. */
  private withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T | null> {
    return Promise.race([
      promise,
      new Promise<null>(resolve => setTimeout(() => {
        this.iosDebugStep(`⏱ TIMED OUT after ${ms / 1000}s: ${label}`);
        resolve(null);
      }, ms))
    ]);
  }

  /**
   * Request permission for iOS using Capacitor PushNotifications + AppDelegate FCM token injection.
   * Every step emits an on-screen debug event so the user can see exactly where it hangs.
   */
  requestIOSPermissionWithFirebase = async (userId: string, startTime: number): Promise<{ success: boolean; token?: string; error?: string }> => {
    this.iosDebugStep('Step 1: iOS flow started');

    // Step 2: Verify Firebase is initialized
    const { getApps } = await import('firebase/app');
    if (getApps().length === 0) {
      this.iosDebugStep('Step 2: ❌ Firebase not initialized');
      return { success: false, error: 'Firebase not initialized. Please wait for app to fully load.' };
    }
    this.iosDebugStep('Step 2: ✅ Firebase initialized');

    // Step 3: Reuse any token already captured by native listeners
    const existingToken = this.pendingTokenToSave || this.nativeTokenReceived || (window as any).__FCM_TOKEN__;
    if (existingToken) {
      this.iosDebugStep(`Step 3: ✅ Token already in memory (${existingToken.substring(0, 12)}...) — saving`);
      try {
        await this.saveTokenToDatabase(userId, existingToken);
        this.pendingTokenToSave = null;
        this.nativeTokenReceived = null;
        this.iosDebugStep('Step 3: ✅ Token saved — done!');
        return { success: true, token: existingToken };
      } catch (saveError) {
        this.iosDebugStep(`Step 3: ❌ Save failed: ${saveError instanceof Error ? saveError.message : String(saveError)}`);
        return { success: false, error: `Token save failed: ${saveError instanceof Error ? saveError.message : 'Unknown'}` };
      }
    }
    this.iosDebugStep('Step 3: No cached token — requesting permission');

    // Step 4: Check current permission status (10 s timeout)
    this.iosDebugStep('Step 4: Calling PushNotifications.checkPermissions()...');
    const permStatus = await this.withTimeout(PushNotifications.checkPermissions(), 10000, 'checkPermissions');
    if (!permStatus) {
      this.iosDebugStep('Step 4: ❌ checkPermissions() timed out — Capacitor bridge may be stuck');
      return { success: false, error: 'checkPermissions() timed out. Try force-closing and reopening the app.' };
    }
    this.iosDebugStep(`Step 4: ✅ Permission status = "${permStatus.receive}"`);

    // Step 5: Request permission if needed
    let finalStatus = permStatus;
    if (permStatus.receive === 'prompt') {
      this.iosDebugStep('Step 5: Calling PushNotifications.requestPermissions()...');
      const requested = await this.withTimeout(PushNotifications.requestPermissions(), 30000, 'requestPermissions');
      if (!requested) {
        this.iosDebugStep('Step 5: ❌ requestPermissions() timed out');
        return { success: false, error: 'requestPermissions() timed out. The system dialog may not have appeared.' };
      }
      finalStatus = requested;
      this.iosDebugStep(`Step 5: ✅ After request = "${finalStatus.receive}"`);
    } else {
      this.iosDebugStep('Step 5: Skipped (already decided)');
    }

    if (finalStatus.receive !== 'granted') {
      this.iosDebugStep(`Step 5: ❌ Permission not granted (${finalStatus.receive})`);
      return { success: false, error: 'Notification permission denied by user' };
    }

    // Step 6: Register for APNs/FCM — AppDelegate will inject the token into window.__FCM_TOKEN__
    this.iosDebugStep('Step 6: Calling PushNotifications.register()...');
    const registered = await this.withTimeout(PushNotifications.register(), 10000, 'register');
    if (registered === null) {
      this.iosDebugStep('Step 6: ⚠️ register() timed out, but continuing to poll for token anyway');
    } else {
      this.iosDebugStep('Step 6: ✅ register() resolved');
    }

    // Step 7: Poll window.__FCM_TOKEN__ for up to 10 s (AppDelegate injects it)
    this.iosDebugStep('Step 7: Polling for FCM token (max 10 s)...');
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 500));
      const polledToken = (window as any).__FCM_TOKEN__;
      if (polledToken) {
        this.iosDebugStep(`Step 7: ✅ Token found at poll #${i + 1} — saving...`);
        try {
          await this.saveTokenToDatabase(userId, polledToken);
          this.pendingTokenToSave = null;
          this.nativeTokenReceived = null;
          this.iosDebugStep('Step 7: ✅ Token saved — done!');
          return { success: true, token: polledToken };
        } catch (saveErr) {
          this.iosDebugStep(`Step 7: ❌ Save failed: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}`);
          return { success: false, error: `Token found but save failed: ${saveErr instanceof Error ? saveErr.message : 'Unknown'}` };
        }
      }
      if (i % 4 === 3) this.iosDebugStep(`Step 7: Still waiting... ${(i + 1) * 500 / 1000}s elapsed`);
    }
    this.iosDebugStep('Step 7: No token after 10 s polling — waiting for event (20 s max)...');

    // Step 8: Event-listener fallback for remaining time
    return new Promise(resolve => {
      let resolved = false;
      const cleanup = () => {
        window.removeEventListener('FirebaseMessagingTokenReceived', onTokenReceived);
        window.removeEventListener('FirebaseMessagingRegistrationError', onTokenError);
        clearTimeout(timeout);
      };

      const onTokenReceived = async (event: any) => {
        if (resolved) return;
        const token = event.detail?.fcmToken || event.detail?.token || (event as any).fcmToken;
        if (!token) return;
        this.iosDebugStep(`Step 8: ✅ Token received via event — saving...`);
        try {
          await this.saveTokenToDatabase(userId, token);
          this.pendingTokenToSave = null;
          this.nativeTokenReceived = null;
          resolved = true;
          cleanup();
          this.iosDebugStep('Step 8: ✅ Token saved — done!');
          resolve({ success: true, token });
        } catch (saveError) {
          resolved = true;
          cleanup();
          this.iosDebugStep(`Step 8: ❌ Save failed: ${saveError instanceof Error ? saveError.message : String(saveError)}`);
          resolve({ success: false, error: `Event token save failed: ${saveError instanceof Error ? saveError.message : 'Unknown'}` });
        }
      };

      const onTokenError = (event: any) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        const msg = event?.detail?.error || event?.error || 'APNs registration failed';
        this.iosDebugStep(`Step 8: ❌ Registration error: ${msg}`);
        resolve({ success: false, error: msg });
      };

      const timeout = setTimeout(() => {
        if (resolved) return;
        resolved = true;
        cleanup();
        this.iosDebugStep('Step 8: ❌ FINAL TIMEOUT — AppDelegate never delivered a token');
        resolve({ success: false, error: 'iOS never delivered an FCM token. See debug steps above for where it stopped.' });
      }, 20000);

      window.addEventListener('FirebaseMessagingTokenReceived', onTokenReceived);
      window.addEventListener('FirebaseMessagingRegistrationError', onTokenError);
    });
  };

  /**
   * Request permission for Android using Capacitor Push Notifications plugin
   */
  requestAndroidPermissionWithCapacitor = async (userId: string, startTime: number): Promise<{ success: boolean; token?: string; error?: string }> => {
    try {
      const platform = Capacitor.getPlatform();
      console.log('[NotificationService] Using Capacitor Push Notifications plugin for Android');
      
      // Phase 2: Verify Firebase Initialization
    console.log('[NotificationService] Phase 2: Verifying Firebase initialization...');
    const { getApps } = await import('firebase/app');
    const firebaseApps = getApps();
    console.log('[NotificationService] Firebase apps initialized:', firebaseApps.length);
    
    if (firebaseApps.length === 0) {
      console.error('[NotificationService] Firebase not initialized!');
      return {
        success: false,
        error: 'Firebase not initialized. Please wait for app to fully load.'
      };
    }
    console.log('[NotificationService] Firebase is initialized ✓');
    
    // Verify PushNotifications plugin is available
    console.log('[NotificationService] Verifying PushNotifications plugin...');
    if (!PushNotifications) {
      console.error('[NotificationService] PushNotifications plugin not available!');
      return {
        success: false,
        error: 'Push Notifications plugin not available. Make sure @capacitor/push-notifications is installed.'
      };
    }
    
    console.log('[NotificationService] PushNotifications plugin is available ✓');
    console.log('[NotificationService] Time elapsed so far:', Date.now() - startTime, 'ms');
    
      // Check current permission status
    console.log('[NotificationService] Checking current permissions...');
    const permCheckStart = Date.now();
      let permStatus = await PushNotifications.checkPermissions();
    console.log('[NotificationService] Permission check took:', Date.now() - permCheckStart, 'ms');
    console.log('[NotificationService] Permission status:', JSON.stringify(permStatus));
      
      // Request permission if not already granted
      if (permStatus.receive === 'prompt') {
      console.log('[NotificationService] Requesting permissions...');
      const permRequestStart = Date.now();
        permStatus = await PushNotifications.requestPermissions();
      console.log('[NotificationService] Permission request took:', Date.now() - permRequestStart, 'ms');
      console.log('[NotificationService] Permissions granted:', permStatus);
      }

      if (permStatus.receive !== 'granted') {
      console.log('[NotificationService] Permissions denied:', permStatus.receive);
      console.log('[NotificationService] Total time elapsed:', Date.now() - startTime, 'ms');
        return {
          success: false,
          error: 'Notification permission denied'
        };
      }

    console.log('[NotificationService] Permissions granted ✓');
    console.log('[NotificationService] Time elapsed so far:', Date.now() - startTime, 'ms');
    console.log('[NotificationService] Registering for push notifications...');
    
    // Wait for app to be fully loaded
    console.log('[NotificationService] Phase 7: Waiting for app to be fully loaded...');
    await new Promise(resolve => setTimeout(resolve, 500));
    console.log('[NotificationService] App initialization wait complete');
    console.log('[NotificationService] Time elapsed so far:', Date.now() - startTime, 'ms');

      // Wait for registration token
      // The token comes via the 'registration' event listener
      // IMPORTANT: Set up listeners BEFORE calling register() to avoid race condition
      return await new Promise(async (resolve) => {
        let isResolved = false;
        const registrationStartTime = Date.now();
        
        // Set up a one-time listener for registration FIRST
        console.log('[NotificationService] Setting up registration listener BEFORE register()...');
        const listenerSetupStart = Date.now();
        // addListener returns PluginListenerHandle directly, not a Promise
        // @ts-expect-error - Capacitor type definitions are incorrect, addListener returns PluginListenerHandle not Promise
        const registrationListener: any = PushNotifications.addListener('registration', async (token) => {
          const tokenReceivedTime = Date.now();
          const timeSinceRegistration = tokenReceivedTime - registrationStartTime;
          console.log('[NotificationService] ========================================');
          console.log('[NotificationService] ✅ REGISTRATION EVENT RECEIVED!');
          console.log('[NotificationService] Time since register() called:', timeSinceRegistration, 'ms');
          console.log('[NotificationService] Total time since start:', tokenReceivedTime - startTime, 'ms');
          if (isResolved) {
            console.log('[NotificationService] Registration event received but already resolved (duplicate event)');
            return;
          }
          
          console.log('[NotificationService] Token value length:', token.value?.length || 0);
          console.log('[NotificationService] Token preview:', token.value?.substring(0, 40) + '...');
          isResolved = true;
          
          const saveStartTime = Date.now();
          try {
            console.log('[NotificationService] Saving token to Firestore...');
            // Save token to user document
            await this.saveTokenToDatabase(userId, token.value);
            const saveDuration = Date.now() - saveStartTime;
            console.log('[NotificationService] Token saved successfully in', saveDuration, 'ms');
            console.log('[NotificationService] Push token obtained and saved:', token.value);
            console.log('[NotificationService] Total registration time:', Date.now() - startTime, 'ms');
            console.log('[NotificationService] ========================================');
            
            // Store token for future use
            this.nativeTokenReceived = token.value;
            
            // Remove this listener
            await removeListener(registrationListener);
            
            resolve({
              success: true,
              token: token.value
            });
          } catch (error) {
            const saveDuration = Date.now() - saveStartTime;
            console.error('[NotificationService] Error saving token after', saveDuration, 'ms:', error);
            console.error('[NotificationService] Error details:', {
              message: error instanceof Error ? error.message : 'Unknown error',
              stack: error instanceof Error ? error.stack : undefined
            });
            await removeListener(registrationListener);
            resolve({
              success: false,
              error: error instanceof Error ? error.message : 'Failed to save token'
            });
          }
        });
        const listenerSetupDuration = Date.now() - listenerSetupStart;
        console.log('[NotificationService] Registration listener setup took:', listenerSetupDuration, 'ms');

        // Set up error listener
        console.log('[NotificationService] Setting up error listener...');
        const errorListenerSetupStart = Date.now();
        // @ts-expect-error - Capacitor type definitions are incorrect, addListener returns PluginListenerHandle not Promise
        const errorListener: any = PushNotifications.addListener('registrationError', async (error) => {
          const errorReceivedTime = Date.now();
          const timeSinceRegistration = errorReceivedTime - registrationStartTime;
          console.error('[NotificationService] ========================================');
          console.error('[NotificationService] ❌ REGISTRATION ERROR RECEIVED!');
          console.error('[NotificationService] Time since register() called:', timeSinceRegistration, 'ms');
          console.error('[NotificationService] Total time since start:', errorReceivedTime - startTime, 'ms');
          
          if (isResolved) {
            console.log('[NotificationService] Registration error received but already resolved (duplicate event)');
            return;
          }
          
          console.error('[NotificationService] Error object:', JSON.stringify(error, null, 2));
          console.error('[NotificationService] Error type:', typeof error);
          console.error('[NotificationService] Error keys:', Object.keys(error || {}));
          
          isResolved = true;
          
          try {
            await removeListener(errorListener);
            await removeListener(registrationListener);
          } catch (removeError) {
            console.warn('[NotificationService] Error removing listeners:', removeError);
          }
          
          let errorMessage = 'Failed to register for push notifications';
          
          // Check if this is a simulator error
          if (error.error && typeof error.error === 'string') {
            if (error.error.includes('simulator') || error.error.includes('Simulator')) {
              errorMessage = 'Push notifications do not work on iOS Simulator. Please test on a physical device.';
            } else {
              errorMessage = error.error;
            }
          } else if (error && typeof error === 'object') {
            // Try to extract error message from various possible structures
            errorMessage = (error as any).message || (error as any).error || JSON.stringify(error);
          }
          
          console.error('[NotificationService] Final error message:', errorMessage);
          console.error('[NotificationService] ========================================');
          
          resolve({
            success: false,
            error: errorMessage
          });
        });
        const errorListenerSetupDuration = Date.now() - errorListenerSetupStart;
        console.log('[NotificationService] Error listener setup took:', errorListenerSetupDuration, 'ms');

        // NOW call register() after listeners are set up
        console.log('[NotificationService] ========================================');
        console.log('[NotificationService] Listeners set up, now calling register()...');
        console.log('[NotificationService] Time elapsed so far:', Date.now() - startTime, 'ms');
        console.log('[NotificationService] Waiting 100ms before register() to ensure listeners are fully ready...');
        
        // Small delay to ensure listeners are fully registered in native layer
        await new Promise(resolve => setTimeout(resolve, 100));
        console.log('[NotificationService] Delay complete, proceeding with register()...');
        
        const registerCallStart = Date.now();
        try {
          console.log('[NotificationService] Calling PushNotifications.register() now...');
          console.log('[NotificationService] Timestamp:', new Date().toISOString());
          await PushNotifications.register();
          const registerCallDuration = Date.now() - registerCallStart;
          console.log('[NotificationService] Register() call completed in', registerCallDuration, 'ms');
          console.log('[NotificationService] Register() call successful (returns void, token comes via event)');
          console.log('[NotificationService] Waiting for registration event from APNs...');
          console.log('[NotificationService] Time elapsed so far:', Date.now() - startTime, 'ms');
          console.log('[NotificationService] ========================================');
          
        } catch (registerError) {
          const registerCallDuration = Date.now() - registerCallStart;
          console.error('[NotificationService] ========================================');
          console.error('[NotificationService] ❌ Register() call failed!');
          console.error('[NotificationService] Call duration:', registerCallDuration, 'ms');
          console.error('[NotificationService] Error:', registerError);
          console.error('[NotificationService] Error type:', typeof registerError);
          console.error('[NotificationService] Error message:', registerError instanceof Error ? registerError.message : 'Unknown');
          console.error('[NotificationService] Error stack:', registerError instanceof Error ? registerError.stack : 'N/A');
          console.error('[NotificationService] ========================================');
          
          isResolved = true;
          try {
            await removeListener(errorListener);
            await removeListener(registrationListener);
          } catch (removeError) {
            console.warn('[NotificationService] Error removing listeners:', removeError);
          }
          resolve({
            success: false,
            error: `Failed to call register(): ${registerError instanceof Error ? registerError.message : 'Unknown error'}`
          });
          return;
        }

        // Timeout after 20 seconds (increased to give APNs more time)
        const timeoutDuration = 20000; // 20 seconds
        console.log('[NotificationService] Starting', timeoutDuration / 1000, 'second timeout timer...');
        console.log('[NotificationService] Timeout will fire at:', new Date(Date.now() + timeoutDuration).toISOString());
        
        setTimeout(async () => {
          const timeoutFiredTime = Date.now();
          const totalElapsed = timeoutFiredTime - startTime;
          const timeSinceRegister = timeoutFiredTime - registrationStartTime;
          
          console.error('[NotificationService] ========================================');
          console.error('[NotificationService] ⏱️ TIMEOUT FIRED!');
          console.error('[NotificationService] Total time elapsed:', totalElapsed, 'ms');
          console.error('[NotificationService] Time since register() called:', timeSinceRegister, 'ms');
          console.error('[NotificationService] Timeout duration:', timeoutDuration, 'ms');
          
          if (isResolved) {
            console.log('[NotificationService] Timeout fired but already resolved (token received or error occurred)');
            return;
          }
          
          console.error('[NotificationService] register() was called but APNs did not return a token');
          console.error('[NotificationService] This suggests APNs is silently rejecting the registration');
          console.error('[NotificationService] Possible causes:');
          console.error('[NotificationService] 1. APNs key not properly linked to App ID');
          console.error('[NotificationService] 2. App ID configuration issue');
          console.error('[NotificationService] 3. Provisioning profile mismatch');
          console.error('[NotificationService] 4. Capacitor plugin compatibility issue');
          console.error('[NotificationService] 5. Missing iOS delegate methods in AppDelegate');
          console.error('[NotificationService] 6. Firebase not fully initialized');
          console.error('[NotificationService] 7. Network connectivity issue to APNs servers');
          console.error('[NotificationService] ========================================');
          
          isResolved = true;
          
          try {
            await removeListener(registrationListener);
          } catch (removeError) {
            console.warn('[NotificationService] Error removing registration listener:', removeError);
          }
          try {
            if (errorListener && typeof (errorListener as any).remove === 'function') {
              // @ts-ignore - PluginListenerHandle.remove() exists at runtime
              await (errorListener as any).remove();
            }
          } catch (removeError) {
            console.warn('[NotificationService] Error removing error listener:', removeError);
          }
          
          // Provide more helpful error message
          let errorMessage = `Registration timeout - token not received after ${timeoutDuration / 1000} seconds. `;
          
          if (platform === 'ios') {
            errorMessage += 'This usually means:\n' +
              '1. Running on iOS Simulator (not supported - use physical device)\n' +
              '2. Push Notifications capability not enabled in Xcode\n' +
              '3. GoogleService-Info.plist not properly configured\n' +
              '4. APNs authentication key not uploaded to Firebase Console\n' +
              '5. Missing delegate methods in AppDelegate.swift\n' +
              '6. APNs key not properly linked to App ID';
          } else {
            errorMessage += 'Check that Google Services is properly configured in your Android app.';
          }
          
          resolve({
            success: false,
            error: errorMessage
          });
        }, timeoutDuration);
      });
    } catch (error) {
      console.error('[NotificationService] Error requesting native notification permission:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  /**
   * Save FCM token to user document in Firestore
   * Stores tokens per platform to support multiple devices
   * Uses REST API on iOS to avoid Firebase SDK issues
   */
  saveTokenToDatabase = async (userId: string, token: string): Promise<void> => {
    const platform = Capacitor.getPlatform();
    
    console.log('[NotificationService] ========================================');
    console.log('[NotificationService] 🔥 saveTokenToDatabase CALLED');
    console.log('[NotificationService] Platform:', platform);
    console.log('[NotificationService] User ID:', userId);
    console.log('[NotificationService] Token length:', token.length);
    console.log('[NotificationService] Token preview:', token.substring(0, 50) + '...');
    console.log('[NotificationService] ========================================');
    
    if (!userId) {
      const error = new Error('User ID is required to save FCM token');
      console.error('[NotificationService] ❌', error.message);
      throw error;
    }
    
    if (!token) {
      const error = new Error('FCM token is required');
      console.error('[NotificationService] ❌', error.message);
      throw error;
    }
    
    try {
      
      // On iOS the Firestore SDK silently hangs — skip it entirely and use REST API directly.
      if (platform === 'ios') {
        this.iosDebugStep('Save: Reading existing tokens via REST API...');
        let existingTokensForRest: Record<string, string> = {};
        try {
          const existingDoc = await firestoreRestApi.getDocument('users', userId);
          if (existingDoc && existingDoc.fcmTokens && typeof existingDoc.fcmTokens === 'object') {
            existingTokensForRest = existingDoc.fcmTokens as Record<string, string>;
          }
          this.iosDebugStep('Save: ✅ Existing tokens read');
        } catch (readErr) {
          this.iosDebugStep(`Save: ⚠️ Could not read existing tokens: ${readErr instanceof Error ? readErr.message : String(readErr)}`);
        }
        const mergedTokens = { ...existingTokensForRest, [platform]: token };
        this.iosDebugStep('Save: Writing token via REST API...');
        await firestoreRestApi.setDocument('users', userId, {
          fcmTokens: mergedTokens,
          fcmToken: token,
          notificationsEnabled: true,
          updatedAt: new Date().toISOString()
        }, true);
        this.iosDebugStep('Save: ✅ Token written to Firestore');
        return;
      }
      
      // Web/Android: Use Firebase SDK field-level update so we never overwrite other platforms' tokens
      console.log('[NotificationService] Using Firebase SDK to save token (field-level update)');
      const userRef = doc(db, 'users', userId);
      
      try {
        // Use dot-notation key so only THIS platform's slot is written; other platforms are untouched
        await updateDoc(userRef, {
          [`fcmTokens.${platform}`]: token,
          fcmToken: token, // Keep legacy field for backward compatibility
          notificationsEnabled: true,
          updatedAt: new Date()
        });
        
        console.log(`[NotificationService] Token saved successfully via Firebase SDK for platform: ${platform}`);
      } catch (sdkError: any) {
        // If SDK fails with permission error, try REST API fallback
        if (sdkError?.code === 'permission-denied') {
          console.warn('[NotificationService] SDK permission denied for saveTokenToDatabase; retrying via REST API');
          try {
            // Read existing tokens first so we don't overwrite other platforms
            let existingTokensForRest: Record<string, string> = {};
            try {
              const existingDoc = await firestoreRestApi.getDocument('users', userId);
              if (existingDoc && existingDoc.fcmTokens && typeof existingDoc.fcmTokens === 'object') {
                existingTokensForRest = existingDoc.fcmTokens as Record<string, string>;
              }
            } catch (readErr) {
              console.warn('[NotificationService] Could not read existing tokens for REST merge:', readErr);
            }
            const mergedTokens = { ...existingTokensForRest, [platform]: token };
            await firestoreRestApi.setDocument('users', userId, {
              fcmTokens: mergedTokens,
              fcmToken: token,
              notificationsEnabled: true,
              updatedAt: new Date().toISOString()
            }, true);
            console.log(`[NotificationService] Token saved successfully via REST API fallback for platform: ${platform}`);
            return;
          } catch (restError) {
            console.error('[NotificationService] REST API fallback for saveTokenToDatabase also failed:', restError);
            throw restError;
          }
        }
        // Re-throw other SDK errors
        throw sdkError;
      }
    } catch (error) {
      console.error('[NotificationService] Error saving token to database:', error);
      throw error;
    }
  };

  /**
   * Silently refresh and save the native FCM token on app startup.
   * Called when a user with notificationsEnabled=true logs in on Android/iOS.
   * Does NOT re-request permissions — only re-registers to get the current token.
   */
  refreshNativeToken = async (userId: string): Promise<void> => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') return;

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive !== 'granted') {
        console.log('[NotificationService] refreshNativeToken: permissions not granted, skipping');
        return;
      }

      if (platform === 'ios') {
        // On iOS, the Capacitor 'registration' event fires with the raw APNs device token
        // (a hex string), NOT the FCM registration token. Saving it would cause
        // messaging/invalid-argument errors in Cloud Functions.
        // The real FCM token is dispatched to window.__FCM_TOKEN__ by AppDelegate.swift
        // via messaging(_:didReceiveRegistrationToken:). Use that instead.

        // If the FCM token is already in memory (set by AppDelegate on launch), save it now.
        const existingToken = (window as any).__FCM_TOKEN__ || this.pendingTokenToSave || this.nativeTokenReceived;
        if (existingToken) {
          console.log('[NotificationService] refreshNativeToken iOS: using existing FCM token from memory');
          try {
            await this.saveTokenToDatabase(userId, existingToken);
            console.log('[NotificationService] refreshNativeToken iOS: token saved successfully');
          } catch (err) {
            console.error('[NotificationService] refreshNativeToken iOS: failed to save token:', err);
          }
          return;
        }

        // Token not in memory yet — trigger register() and poll window.__FCM_TOKEN__
        // (AppDelegate will inject the real FCM token within a few seconds)
        console.log('[NotificationService] refreshNativeToken iOS: no cached token, calling register() and polling...');
        PushNotifications.register().catch((err) => {
          console.warn('[NotificationService] refreshNativeToken iOS: register() error (non-fatal):', err);
        });

        await new Promise<void>((resolve) => {
          let done = false;
          const timeout = setTimeout(() => {
            if (!done) { done = true; resolve(); }
          }, 10000);

          // Poll window.__FCM_TOKEN__ every 500ms for up to 10s
          const poll = setInterval(async () => {
            const fcmToken = (window as any).__FCM_TOKEN__;
            if (fcmToken && !done) {
              done = true;
              clearInterval(poll);
              clearTimeout(timeout);
              try {
                await this.saveTokenToDatabase(userId, fcmToken);
                console.log('[NotificationService] refreshNativeToken iOS: FCM token saved via polling');
              } catch (err) {
                console.error('[NotificationService] refreshNativeToken iOS: save error:', err);
              }
              resolve();
            }
          }, 500);

          // Also listen for the event dispatched by AppDelegate
          const onToken = async (event: any) => {
            const token = event.detail?.fcmToken || event.detail?.token;
            if (token && !done) {
              done = true;
              clearInterval(poll);
              clearTimeout(timeout);
              window.removeEventListener('FirebaseMessagingTokenReceived', onToken);
              try {
                await this.saveTokenToDatabase(userId, token);
                console.log('[NotificationService] refreshNativeToken iOS: FCM token saved via event');
              } catch (err) {
                console.error('[NotificationService] refreshNativeToken iOS: save error:', err);
              }
              resolve();
            }
          };
          window.addEventListener('FirebaseMessagingTokenReceived', onToken);
        });
      } else {
        // Android: Capacitor 'registration' event gives the real FCM token directly
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            console.warn('[NotificationService] refreshNativeToken: token refresh timed out');
            resolve();
          }, 10000);

          PushNotifications.addListener('registration', async (token) => {
            clearTimeout(timeout);
            console.log(`[NotificationService] refreshNativeToken: received token for ${platform}`);
            try {
              await this.saveTokenToDatabase(userId, token.value);
              console.log('[NotificationService] refreshNativeToken: token saved successfully');
            } catch (err) {
              console.error('[NotificationService] refreshNativeToken: failed to save token:', err);
            }
            resolve();
          });

          PushNotifications.register().catch((err) => {
            clearTimeout(timeout);
            console.error('[NotificationService] refreshNativeToken: register() failed:', err);
            resolve();
          });
        });
      }
    } catch (err) {
      console.error('[NotificationService] refreshNativeToken error:', err);
    }
  };

  /**
   * Disable notifications for a user
   */
  disableNotifications = async (userId: string): Promise<void> => {
    const platform = Capacitor.getPlatform();
    // On iOS the Firestore SDK silently hangs — use REST API directly
    if (platform === 'ios') {
      await firestoreRestApi.setDocument('users', userId, {
        notificationsEnabled: false,
        updatedAt: new Date().toISOString()
      }, true);
      return;
    }
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        notificationsEnabled: false,
        updatedAt: new Date()
      });
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        await firestoreRestApi.setDocument('users', userId, {
          notificationsEnabled: false,
          updatedAt: new Date().toISOString()
        }, true);
        return;
      }
      console.error('Error disabling notifications:', error);
      throw error;
    }
  };

  /**
   * Enable notifications for a user (if token exists)
   */
  enableNotifications = async (userId: string): Promise<void> => {
    const platform = Capacitor.getPlatform();
    // On iOS the Firestore SDK silently hangs — use REST API directly
    if (platform === 'ios') {
      await firestoreRestApi.setDocument('users', userId, {
        notificationsEnabled: true,
        updatedAt: new Date().toISOString()
      }, true);
      return;
    }
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        notificationsEnabled: true,
        updatedAt: new Date()
      });
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        await firestoreRestApi.setDocument('users', userId, {
          notificationsEnabled: true,
          updatedAt: new Date().toISOString()
        }, true);
        return;
      }
      console.error('Error enabling notifications:', error);
      throw error;
    }
  };

  /**
   * Setup listener for foreground messages
   */
  setupMessageListener = (): void => {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      
      // Show notification when app is in foreground
      if (payload.notification) {
        const { title, body } = payload.notification;
        
        // Create a notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(title || 'Streaker', {
            body: body || '',
            icon: '/logo192.png',
            badge: '/logo192.png',
            requireInteraction: true, // Keep notification visible longer
            tag: 'running-tracker-notification' // Replace previous notifications
          });
        }
      }
    });
  };

  /**
   * Check if notifications are supported and permission status
   */
  getNotificationStatus = async (): Promise<{ supported: boolean; permission: string | null }> => {
    const platform = Capacitor.getPlatform();
    
    console.log('Checking notification status for platform:', platform);
    
    if (platform === 'web') {
      // Web: Check browser notification support
      if (!('Notification' in window)) {
        return { supported: false, permission: null };
      }

      return {
        supported: true,
        permission: Notification.permission
      };
    } else {
      // iOS/Android: Native push notifications are always supported via Capacitor
      console.log('Native platform detected, push notifications supported');
      try {
        const permStatus = await PushNotifications.checkPermissions();
        console.log('Push notification permission status:', permStatus);
        return {
          supported: true,
          permission: permStatus.receive || 'prompt'
        };
      } catch (error) {
        console.error('Error checking push notification permissions:', error);
        // Even if checking permissions fails, native push is still supported
        // The error might be due to iOS simulator or permissions not yet requested
        return { 
          supported: true, // Changed from false to true for native platforms
          permission: 'prompt' 
        };
      }
    }
  };

  /**
   * Cleanup listeners (call when service is destroyed)
   */
  cleanup = (): void => {
    // Remove all push notification listeners
    this.pushNotificationListeners.forEach(listener => {
      try {
        listener.remove();
      } catch (error) {
        console.error('Error removing push notification listener:', error);
      }
    });
    this.pushNotificationListeners = [];
  };
}

export const notificationService = new NotificationService();


